// ---------------------------------------------------------------------------
// PlatformRestFallback — KgDesignSystemFallback backed by platform.cogo.xyz.
//
// Phase 1b Step 1b.4 per .planning/kg-repository-interface.md v1.0 §7.1.
//
// Studio today calls `GET {platformBase}/api/design-systems` to populate
// the DS picker (fetchPresetsViaWorker pattern). Phase 1b's HybridRepository
// accepts this class as the DS fallback — local WASM is the preferred path,
// but until per-DS shards land (Step 5-6 scope) the Core shard has zero
// DS instances, so HybridRepository MUST fall through here.
//
// Response shape (observed — Studio fetch-presets-via-worker.ts):
//   [ { id, name, description } ]  // PresetListItem[]
//
// Extended to DesignSystem by synthesizing the IRI from `id`.
// ---------------------------------------------------------------------------

import type { DesignSystem } from "../types"
import type { KgDesignSystemFallback } from "./types"

export interface PlatformRestFallbackConfig {
  /**
   * Base URL, e.g. "https://platform.cogo.xyz". Paths are appended — this
   * class calls `{base}/api/design-systems` and `{base}/api/design-systems/{id}`.
   */
  baseUrl: string
  /** Optional Bearer token supplier. */
  getAuthToken?: () => Promise<string | null | undefined>
  /** Optional static headers for proxy/connectivity layers. */
  headers?: Record<string, string>
  /** Optional fetch override for testing. */
  fetchImpl?: typeof fetch
  /** Abort timeout (ms). Default: 10000. */
  timeoutMs?: number
}

interface PresetListItem {
  id: string
  name: string
  description?: string
}

/** Build a cogo:DesignSystem IRI from the short id. */
function synthIri(id: string): string {
  return `https://cogo.xyz/ns/v1#DesignSystem-${id}`
}

function toDesignSystem(raw: unknown): DesignSystem | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const id = typeof r.id === "string" ? r.id : null
  const name = typeof r.name === "string" ? r.name : null
  if (!id || !name) return null
  return { id, iri: synthIri(id), name }
}

export class PlatformRestFallback implements KgDesignSystemFallback {
  constructor(private readonly config: PlatformRestFallbackConfig) {}

  async listDesignSystems(): Promise<DesignSystem[]> {
    const arr = await this.call<PresetListItem[]>("/api/design-systems")
    if (!Array.isArray(arr)) return []
    return arr.map(toDesignSystem).filter((d): d is DesignSystem => d !== null)
  }

  async getDesignSystem(id: string): Promise<DesignSystem | null> {
    const safe = encodeURIComponent(id)
    const body = await this.call<PresetListItem | null>(
      `/api/design-systems/${safe}`,
    )
    return toDesignSystem(body)
  }

  // ——— Internals ———

  private async call<T>(path: string): Promise<T | null> {
    const fetcher = this.config.fetchImpl ?? fetch
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(this.config.headers ?? {}),
    }
    if (this.config.getAuthToken) {
      const tok = await this.config.getAuthToken()
      if (tok) headers["Authorization"] = `Bearer ${tok}`
    }
    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(),
      this.config.timeoutMs ?? 10000,
    )
    try {
      const res = await fetcher(`${this.config.baseUrl}${path}`, {
        method: "GET",
        headers,
        signal: controller.signal,
      })
      if (res.status === 404) return null
      // Graceful-degrade on auth-gated endpoints when the user isn't
      // authorized. Anonymous clients (no Supabase session) should see
      // an empty result instead of an exception — matches the
      // `fetchPresetsViaWorker` "no token → return []" semantic used
      // everywhere else in Studio. Phase 2b KgSmoke observed 401 throws
      // preventing `HybridRepository.listDesignSystems()` from falling
      // back gracefully; see `.planning/phase-2b-judge-first-consumer-results.md`
      // §5.4 #1.
      if (res.status === 401 || res.status === 403) return null
      if (!res.ok) {
        throw new Error(
          `[kg-repository platform-rest] GET ${path} HTTP ${res.status} ${res.statusText}`,
        )
      }
      return (await res.json()) as T
    } finally {
      clearTimeout(timer)
    }
  }
}
